import React, { useMemo } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';

import useDrive from '../../hooks/useDrive';
import columns from './DriveColumns';
import EmptyState from './EmptyState';

import {
  TableWrapper,
  StyledTable,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from './Table.styles';

const DriveTable = () => {
  const { items, currentFolder, search, setCurrentFolder, setBreadcrumbs } = useDrive();

  const data = useMemo(() => {
    const filtered = items.filter((item) => {
      const isCurrentFolder = item.parentId === currentFolder;

      const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase());

      return isCurrentFolder && matchesSearch;
    });

    const folders = filtered.filter((item) => item.type === 'folder');

    const files = filtered.filter((item) => item.type === 'file');

    return [...folders, ...files];
  }, [items, currentFolder, search]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleDoubleClick = (row) => {
    const folder = row.original;

    if (folder.type !== 'folder') {
      return;
    }

    if (folder.id === currentFolder) {
      return;
    }

    setCurrentFolder(folder.id);

    setBreadcrumbs((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === folder.id);

      if (existingIndex >= 0) {
        return prev.slice(0, existingIndex + 1);
      }

      return [
        ...prev,
        {
          id: folder.id,
          name: folder.name,
        },
      ];
    });
  };

  if (!data.length) {
    return <EmptyState />;
  }

  return (
    <TableWrapper>
      <StyledTable>
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHeaderCell key={header.id}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHeaderCell>
              ))}
            </TableRow>
          ))}
        </TableHead>

        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id} onDoubleClick={() => handleDoubleClick(row)}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </StyledTable>
    </TableWrapper>
  );
};

export default DriveTable;
